import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../commons/Auth';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { useLoading } from '../../commons/Loading';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import OfficesPresentational from './OfficesPresentational';

const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

const Offices = ({ profile }) => {
  const [{ newToken } = {}] = useAuth();
  const [data, setData] = useState([]);
  const [formattedCSVData, setFormattedCSVData] = useState([]);
  const [total, setTotal] = useState(0);
  const [t] = useTranslation('OFFICES');
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();

  const [query, setQuery] = useQueryParams({
    code: withDefault(StringParam),
    businessName: withDefault(StringParam),
    email: withDefault(StringParam),
    address: withDefault(StringParam),
    contactPerson: withDefault(StringParam),
    associationSearch: withDefault(StringParam),
    offset: NumberParam,
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    office: withDefault(StringParam)
  });

  const {
    code,
    businessName,
    email,
    address,
    contactPerson,
    associationSearch,
    offset,
    limit,
    office
  } = query;

  const fetchOffices = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/offices`,
      {
        headers: { Authorization: newToken },
        params: {
          code,
          businessName,
          email,
          address,
          contactPerson,
          associationSearch,
          office,
          offset,
          limit,
        },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [], headers = {} } = response;

    setData(data);
    setTotal(Number(headers['x-total-count']));
    hideLoading();
  };

  const fetchOfficesForExport = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/offices/export`,
      {
        headers: { Authorization: newToken },
        params: {
          code,
          businessName,
          email,
          address,
          contactPerson,
          associationSearch,
          office,
          offset: 0,
          limit: 5000,
        },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [] } = response;

    setFormattedCSVData(data.formattedCSVData || []);
    hideLoading();
  };

  function onFilter(query) {
    if (
      (query.businessName && query.businessName !== businessName) ||
      (businessName && query.businessName === '') ||
      (query.email && query.email !== email) ||
      (email && query.email === '') ||
      (query.address && query.address !== address) ||
      (address && query.address === '') ||
      (query.contactPerson && query.contactPerson !== contactPerson) ||
      (contactPerson && query.contactPerson === '') ||
      (query.associationSearch &&
        query.associationSearch !== associationSearch) ||
      (associationSearch && query.associationSearch === '') ||
      (query.office &&
        query.office !== office) ||
      (office && query.office === '') ||
      (query.code && query.code !== code) ||
      (code && query.code === '') ||
      (query.limit && query.limit !== limit)
    ) {
      query.offset = 0; // return to first page
    }
    setQuery(query);
  }

  useEffect(() => {
    fetchOffices();
    fetchOfficesForExport();
  }, [
    code,
    businessName,
    email,
    address,
    contactPerson,
    associationSearch,
    office,
    offset,
    limit,
  ]);

  return (
    <OfficesPresentational
      profile={profile}
      data={data}
      formattedData={formattedCSVData}
      total={total}
      query={query}
      onFilter={onFilter}
    />
  );
};

export default Offices;
